/**
 * PRESCRIPTION CART GROUPS — Savor port
 * Keeps `_group_id` bundles (frame + lens fee + transition) atomic in the cart.
 *
 * Strategy: instead of intercepting every removal path (drawer, cart page,
 * quantity steppers), watch the cart state itself. After any `cart:update`,
 * compare against the last known snapshot; if a group lost a member but
 * still has survivors, remove the survivors too, then re-notify the theme.
 * This survives theme updates and works no matter how the item was removed.
 */

(function () {
  'use strict';

  if (window.BV && window.BV.prescCascadeRemove === false) return;

  var lastGroups = null;   /* { groupId: Set(lineKeys) } from previous cart state */
  var reconciling = false; /* prevent loops while we do our own updates */

  function groupsFromCart(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var gid = item.properties && item.properties._group_id;
      if (!gid) return;
      if (!groups[gid]) groups[gid] = [];
      groups[gid].push(item.key);
    });
    return groups;
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function cartSectionIds() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-items-component'))
      .map(function (el) { return el.dataset.sectionId; })
      .filter(Boolean);
  }

  function removeLines(lineKeys) {
    var updates = {};
    lineKeys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { return r.json(); });
  }

  function notifyTheme(cart) {
    var sections = cartSectionIds();
    var sectionsFetch = sections.length
      ? fetch('/?sections=' + sections.join(',')).then(function (r) { return r.json(); }).catch(function () { return null; })
      : Promise.resolve(null);

    return sectionsFetch.then(function (sectionHtml) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'prescription-cart-groups',
          data: {
            source: 'cart-items-component',
            itemCount: cart.item_count,
            sections: sectionHtml || undefined,
          },
        },
      }));
    });
  }

  function reconcile() {
    if (reconciling) return;
    reconciling = true;

    fetchCart()
      .then(function (cart) {
        var current = groupsFromCart(cart);

        /* First observation — just record */
        if (lastGroups === null) {
          lastGroups = current;
          reconciling = false;
          return;
        }

        /* Find groups that shrank but still have survivors */
        var orphans = [];
        Object.keys(lastGroups).forEach(function (gid) {
          var before = lastGroups[gid].length;
          var now = (current[gid] || []).length;
          if (now > 0 && now < before) {
            orphans = orphans.concat(current[gid]);
          }
        });

        if (!orphans.length) {
          lastGroups = current;
          reconciling = false;
          return;
        }

        return removeLines(orphans)
          .then(function (updatedCart) {
            lastGroups = groupsFromCart(updatedCart);
            return notifyTheme(updatedCart);
          })
          .then(function () { reconciling = false; });
      })
      .catch(function (err) {
        console.error('Prescription cart-groups reconcile error:', err);
        reconciling = false;
      });
  }

  /* Watch the theme's cart lifecycle */
  document.addEventListener('cart:update', function (e) {
    /* Ignore our own notifications */
    if (e.detail && e.detail.sourceId === 'prescription-cart-groups') return;
    /* Small delay so Shopify's cart state settles */
    setTimeout(reconcile, 150);
  });

  /* Seed the snapshot on page load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(reconcile, 300); });
  } else {
    setTimeout(reconcile, 300);
  }
})();
